import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { DeleteOutline as DeleteIcon, DescriptionOutlined as ContractIcon, EditOutlined as EditIcon } from '@mui/icons-material';
import SidePanel, { PanelSection } from '../SidePanel';
import StatusChip from '../projects/StatusChip';
import { GREEN, GREEN_DARK, GREEN_SOFT, INK, MUTED } from '../home/homeTheme';
import {
  changeContractForm,
  contractToForm,
  daysToEnd,
  deleteContract,
  EMPTY_CONTRACT_FORM,
  englishLabel,
  formatMoney,
  lookupLabel,
  MONTHLY_RATE_VALUE,
  NOTE_MAX_LENGTH,
  saveContract,
  validateContractForm,
} from '../../utils/contractsService';
import { parseLocalDate } from '../../utils/dateHelpers';

const fmtDay = (v) => (v ? parseLocalDate(String(v).slice(0, 10)).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null);
const fmtStamp = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null);
const euro = (v) => (formatMoney(v) ? `€ ${formatMoney(v)}` : null);

/** Icona del contratto: quadrato verde chiaro con il documento. */
export function ContractBadge({ size = 32 }) {
  return (
    <Box sx={{ width: size, height: size, borderRadius: size > 40 ? 3 : 2, bgcolor: GREEN_SOFT, color: GREEN_DARK, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <ContractIcon sx={{ fontSize: size * 0.56 }} />
    </Box>
  );
}

/** Scadenza: "in 45 days" (arancione entro 90 giorni), "expired 3 days ago" (rosso). */
export function ExpiryHint({ endDate }) {
  const days = daysToEnd(endDate);
  if (days == null) return null;
  const expired = days < 0;
  const soon = !expired && days <= 90;
  const text = expired ? `expired ${-days} day${days === -1 ? '' : 's'} ago` : days === 0 ? 'expires today' : `in ${days} day${days === 1 ? '' : 's'}`;
  return <Box component="span" sx={{ fontSize: 12, fontWeight: expired || soon ? 700 : 400, color: expired ? 'var(--rt-danger)' : soon ? 'var(--rt-amber)' : MUTED }}>{text}</Box>;
}

function Field({ label, children, full }) {
  const empty = children == null || children === '';
  return (
    <Box sx={{ minWidth: 0, gridColumn: full ? '1 / -1' : undefined }}>
      <Typography sx={{ fontSize: 12, color: MUTED }}>{label}</Typography>
      <Box sx={{ fontSize: 13.5, color: empty ? MUTED : INK, mt: 0.25, wordBreak: 'break-word', whiteSpace: 'pre-line' }}>{empty ? 'Not set' : children}</Box>
    </Box>
  );
}
const grid2 = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', rowGap: 1.5, columnGap: 2 };

/** Voci di una tendina: se il valore salvato non è più tra quelli attivi, compare come "(inactive)" (come nella pagina classica). */
function optionItems(options, current, toLabel = (o) => o.label || o.value) {
  const list = [...(options || [])];
  if (current && !list.some((o) => o.value === current)) list.unshift({ value: current, label: `${current} (inactive)` });
  return list.map((o) => (
    <MenuItem key={o.value} value={o.value}>
      {toLabel(o)}
    </MenuItem>
  ));
}

/**
 * Pannello laterale di un contratto: dettagli, progetti con lo stesso Yubiq code, dati di sistema;
 * modifica e creazione (stessi campi obbligatori della pagina classica) ed eliminazione con conferma.
 */
export default function ContractPanel({ open, contract, creating, customers, serviceManagers, lookups, projects, onClose, onSaved, onDeleted, onOpenProject }) {
  const contractId = contract?.id;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(EMPTY_CONTRACT_FORM);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setEditing(!!creating);
    setForm(!creating && contract ? contractToForm(contract) : EMPTY_CONTRACT_FORM);
    setErrors({});
    setMessage(null);
    setConfirmDelete(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId, creating, open]);

  const linkedProjects = useMemo(
    () => (contract?.yubiq_code ? projects.filter((p) => p.yubiq_code && p.yubiq_code === contract.yubiq_code).sort((a, b) => Number(!!b.active) - Number(!!a.active) || String(a.name).localeCompare(String(b.name))) : []),
    [projects, contract],
  );

  if (!contract && !creating) return <SidePanel open={false} onClose={onClose} />;

  const showForm = creating || editing;
  const monthlyRate = form.tipologia_servizio === MONTHLY_RATE_VALUE;
  const change = (field) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => changeContractForm(f, field, value));
    setErrors((x) => ({ ...x, [field]: undefined }));
  };
  const save = async () => {
    const invalid = validateContractForm(form);
    if (invalid) {
      setErrors(invalid.fields);
      setMessage(invalid.message);
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const res = await saveContract(creating ? null : contractId, form);
      setEditing(false);
      onSaved(creating ? res.contract?.id ?? res.id : contractId, creating ? 'Contract created' : 'Contract updated');
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };
  const cancel = () => {
    if (creating) {
      onClose();
      return;
    }
    setEditing(false);
    setForm(contractToForm(contract));
    setErrors({});
    setMessage(null);
  };
  const remove = async () => {
    setSaving(true);
    try {
      await deleteContract(contractId);
      setConfirmDelete(false);
      onDeleted('Contract deleted');
    } catch (err) {
      setConfirmDelete(false);
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  };

  const typeLabel = (v) => lookupLabel(lookups.type, v);
  const text = (field, label, extra = {}) => (
    <TextField label={label} size="small" fullWidth value={form[field]} onChange={change(field)} error={!!errors[field]} helperText={errors[field] || extra.helperText} {...extra} />
  );
  const select = (field, label, items, extra = {}) => text(field, label, { select: true, children: items, ...extra });
  const smOptions = serviceManagers.map((u) => ({ value: u.username || u.email, label: u.username || u.email }));

  return (
    <>
      <SidePanel
        open={open}
        onClose={onClose}
        busy={saving}
        icon={<ContractBadge size={46} />}
        title={creating ? 'New contract' : contract.customer_name || 'Contract'}
        subtitle={creating ? 'Customer-level contract details, fees and renewal' : [contract.yubiq_code, typeLabel(contract.tipologia_servizio)].filter(Boolean).join(' · ') || null}
        actions={
          !showForm && (
            <Button variant="outlined" color="inherit" size="small" startIcon={<EditIcon />} onClick={() => setEditing(true)} sx={{ textTransform: 'none', borderColor: 'var(--rt-pill-border)', fontWeight: 600 }}>
              Edit
            </Button>
          )
        }
        footer={
          showForm && (
            <>
              <Button color="inherit" onClick={cancel} disabled={saving} sx={{ textTransform: 'none' }}>
                Cancel
              </Button>
              <Button variant="contained" color="success" onClick={save} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none', minWidth: 130, bgcolor: GREEN }}>
                {saving ? <CircularProgress size={18} color="inherit" /> : creating ? 'Create contract' : 'Save changes'}
              </Button>
            </>
          )
        }
      >
        {showForm ? (
          <>
            <PanelSection title="Customer & identification">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.75 }}>
                <Autocomplete
                  size="small"
                  options={customers}
                  value={customers.find((c) => String(c.id) === String(form.customer_id)) || null}
                  getOptionLabel={(c) => c.name || ''}
                  isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
                  onChange={(e, c) => change('customer_id')(c ? c.id : '')}
                  renderInput={(params) => <TextField {...params} label="Customer" error={!!errors.customer_id} helperText={errors.customer_id} InputLabelProps={{ ...params.InputLabelProps, shrink: true }} autoFocus={creating} />}
                />
                {text('yubiq_code', 'Yubiq Code', { sx: { '& input': { fontFamily: 'monospace' } } })}
                <Box sx={grid2}>
                  {select('cluster', 'Cluster', optionItems(lookups.cluster, form.cluster))}
                  {select('tipologia_servizio', 'Type', optionItems(lookups.type, form.tipologia_servizio))}
                </Box>
              </Box>
            </PanelSection>
            <PanelSection title="Contacts">
              <Box sx={{ ...grid2, pt: 0.75 }}>
                {text('client_manager', 'Client Manager')}
                {select('service_manager', 'Service Manager', optionItems(smOptions, form.service_manager))}
              </Box>
            </PanelSection>
            <PanelSection title="Economic data">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.75 }}>
                <Box sx={grid2}>
                  {monthlyRate && text('canone_mensile', 'Current Monthly Fee', { type: 'number', inputProps: { step: '0.01' }, InputProps: { startAdornment: <InputAdornment position="start">€</InputAdornment> } })}
                  {text('importo_rinnovo', 'Renewal Monthly Fee', { type: 'number', inputProps: { step: '0.01' }, InputProps: { startAdornment: <InputAdornment position="start">€</InputAdornment> } })}
                </Box>
                {!monthlyRate && <Typography sx={{ fontSize: 12, color: MUTED, mt: -1 }}>Current Monthly Fee is used only for the Monthly Rate type.</Typography>}
                {select('stato_rinnovo', 'Renewal Status', optionItems(lookups.renewal, form.stato_rinnovo, (o) => englishLabel(o.label || o.value)))}
              </Box>
            </PanelSection>
            <PanelSection title="Contract period">
              <Box sx={{ ...grid2, pt: 0.75 }}>
                {text('inizio_contratto', 'Contract Start Date', { type: 'date', InputLabelProps: { shrink: true } })}
                {text('scadenza_contratto', 'Contract End Date', { type: 'date', InputLabelProps: { shrink: true }, inputProps: { min: form.inizio_contratto || undefined } })}
              </Box>
            </PanelSection>
            <PanelSection title="Additional info">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.75 }}>
                {text('description', 'Description', { multiline: true, minRows: 2 })}
                {text('note', 'Note', { inputProps: { maxLength: NOTE_MAX_LENGTH }, helperText: `${form.note.length}/${NOTE_MAX_LENGTH}` })}
              </Box>
            </PanelSection>
            {message && <Alert severity="error">{message}</Alert>}
          </>
        ) : (
          <>
            {message && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {message}
              </Alert>
            )}
            <PanelSection title="Contract">
              <Box sx={grid2}>
                <Field label="Cluster">{lookupLabel(lookups.cluster, contract.cluster)}</Field>
                <Field label="Type">{typeLabel(contract.tipologia_servizio)}</Field>
                <Field label="Renewal status" full>
                  {englishLabel(lookupLabel(lookups.renewal, contract.stato_rinnovo))}
                </Field>
                <Field label="Start date">{fmtDay(contract.inizio_contratto)}</Field>
                <Field label="End date">
                  {fmtDay(contract.scadenza_contratto) && (
                    <>
                      {fmtDay(contract.scadenza_contratto)} <ExpiryHint endDate={contract.scadenza_contratto} />
                    </>
                  )}
                </Field>
              </Box>
            </PanelSection>
            <PanelSection title="Fees">
              <Box sx={grid2}>
                <Field label="Current monthly fee">{contract.tipologia_servizio === MONTHLY_RATE_VALUE ? euro(contract.canone_mensile) : 'Only for Monthly Rate'}</Field>
                <Field label="Renewal monthly fee">{euro(contract.importo_rinnovo)}</Field>
              </Box>
            </PanelSection>
            <PanelSection title="Contacts">
              <Box sx={grid2}>
                <Field label="Client manager">{contract.client_manager}</Field>
                <Field label="Service manager">{contract.service_manager}</Field>
              </Box>
            </PanelSection>
            <PanelSection title="Additional info">
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Field label="Description">{contract.description}</Field>
                <Field label="Note">{contract.note}</Field>
              </Box>
            </PanelSection>
            <PanelSection title={`Projects with this Yubiq code · ${linkedProjects.length}`}>
              {!contract.yubiq_code ? (
                <Typography sx={{ fontSize: 13.5, color: MUTED }}>This contract has no Yubiq code.</Typography>
              ) : !linkedProjects.length ? (
                <Typography sx={{ fontSize: 13.5, color: MUTED }}>No project uses Yubiq code {contract.yubiq_code}.</Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                  {linkedProjects.map((p) => (
                    <ButtonBase
                      key={p.id}
                      onClick={() => onOpenProject(p.id)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-start', textAlign: 'left', borderRadius: 2, px: 0.75, py: 0.6, mx: -0.75, '&:hover': { bgcolor: 'var(--rt-hover)' } }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: p.active ? INK : MUTED }}>
                          {p.name}
                        </Typography>
                        <Typography noWrap sx={{ fontSize: 12, color: MUTED }}>
                          {p.project_code || '—'}
                        </Typography>
                      </Box>
                      <StatusChip active={!!p.active} />
                    </ButtonBase>
                  ))}
                </Box>
              )}
            </PanelSection>
            <PanelSection title="System">
              <Box sx={grid2}>
                <Field label="Contract ID">{contract.id}</Field>
                <Field label="Created">{[fmtStamp(contract.created_at), contract.created_by_username].filter(Boolean).join(' · ')}</Field>
                <Field label="Last updated" full>
                  {[fmtStamp(contract.updated_at), contract.updated_by_username].filter(Boolean).join(' · ')}
                </Field>
              </Box>
            </PanelSection>
            <Box sx={{ pt: 1.5 }}>
              <Button color="error" size="small" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                Delete contract
              </Button>
            </Box>
          </>
        )}
      </SidePanel>

      <Dialog open={confirmDelete} onClose={() => !saving && setConfirmDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete this contract?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {contract?.customer_name}
            {contract?.yubiq_code ? ` · ${contract.yubiq_code}` : ''} will be removed permanently.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirmDelete(false)} disabled={saving} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={remove} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}>
            {saving ? <CircularProgress size={18} color="inherit" /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
